<template>
  <div>
    <div class="datepicker-from-to-legend">
              <span>
                <strong>{{ $t('explorer.from') }}: </strong>{{ humanReadableStartDate }}
                <strong>{{ $t('explorer.to') }}: </strong>{{ humanReadableEndDate }}
              </span>
    </div>
    <div class="datepicker-wraper">
      <div class="datepicker-shortcuts">
        <ul>
          <li
            v-for="(item, index) in getDefaultDateRangesArrayConfiguration"
            :key="index"
            @click="onDefaultDateRangeClick(index)"
            :class="selectedDefaultRangePosition === index ? 'selected' : ''">
            {{ item.name }}
          </li>
        </ul>
      </div>
      <date-range-picker
        v-model="selectedDates"
        v-on:input="onUserSelectDate"
        class="date-range-picker"
        :disabledDate="disabledDate"
        formatDate="DD MMMM YYYY / HH:mm:ss"
        :startDate="selectedDates[0].toString()"
        type="inline"/>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import DateRangePicker from '@/components/shared/DateRangePicker';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';

export default {
  components: {
    DateRangePicker
  },
  props: {
    value: {
      type: Array
    }
  },
  watch: {
    value: {
      handler () {
        this.selectedDates = this.value;
      },
      deep: true
    }
  },
  data () {
    return {
      selectedDates: [],
      selectedDefaultRangePosition: 0
    };
  },
  created () {
    this.selectedDates = this.getDefaultDateRangesArrayConfiguration[this.selectedDefaultRangePosition].dates;
    this.$emit('input', this.selectedDates);
    dayjs.extend(localizedFormat);
  },
  computed: {
    humanReadableStartDate () {
      const languageCode = this.getLanguageCode;
      return dayjs(this.selectedDates[0]).locale(languageCode).format('LLL');
    },
    humanReadableEndDate () {
      const languageCode = this.getLanguageCode;
      return dayjs(this.selectedDates[1]).locale(languageCode).format('LLL');
    },
    getDefaultDateRangesArrayConfiguration () {
      return defaultDateRangesArrayConfiguration(this);
    },
    ...mapGetters([
      'getLanguageCode'
    ])
  },
  methods: {
    selectCustomRange () {
      this.selectedDefaultRangePosition = this.getDefaultDateRangesArrayConfiguration.length - 1;
    },
    onDefaultDateRangeClick (position) {
      this.selectedDefaultRangePosition = position;
      this.selectedDates = this.getDefaultDateRangesArrayConfiguration[position].dates;
      this.$emit('input', this.selectedDates);
    },
    onUserSelectDate () {
      this.selectCustomRange();
      this.$emit('input', this.selectedDates);
    },
    disabledDate (date) {
      return date > new Date();
    }
  }
};

const defaultDateRangesArrayConfiguration = (context) => {
  return [
    {
      name: context.$t('explorer.defaultRangeDate.today'),
      dates: [
        dayjs().startOf('day').second(0).toDate(),
        dayjs().second(0).toDate()
      ]
    },
    {
      name: context.$t('explorer.defaultRangeDate.yesterday'),
      dates: [
        dayjs().startOf('day').subtract(1, 'days').second(0).toDate(),
        dayjs().endOf('day').subtract(1, 'days').second(0).toDate()
      ]
    },
    {
      name: context.$t('explorer.defaultRangeDate.last7Days'),
      dates: [
        dayjs().subtract(7, 'days').second(0).toDate(),
        dayjs().second(0).toDate()
      ]
    },
    {
      name: context.$t('explorer.defaultRangeDate.last30Days'),
      dates: [
        dayjs().subtract(30, 'days').second(0).toDate(),
        dayjs().second(0).toDate()
      ]
    },
    {
      name: context.$t('explorer.defaultRangeDate.thisMonth'),
      dates: [
        dayjs().startOf('month').second(0).toDate(),
        dayjs().second(0).toDate()
      ]
    },
    {
      name: context.$t('explorer.defaultRangeDate.lastMonth'),
      dates: [
        dayjs().startOf('month').subtract(1, 'month').second(0).toDate(),
        dayjs().subtract(1, 'month').endOf('month').second(0).toDate()
      ]
    },
    {
      name: context.$t('explorer.defaultRangeDate.custom'),
      dates: [
        dayjs().subtract(7, 'days').second(0).toDate(),
        dayjs().second(0).toDate()
      ]
    }
  ];
};
</script>

<style lang="scss" scoped>
  @import "../../stylesheets/variables.scss";

  .datepicker-from-to-legend {
    display: flex;
    align-items: center;
  }

  .datepicker-calendar-icon {
    display: inline-block;
    width: 34px;
    height: 34px;
    background: url('data:image/svg+xml;base64,PHN2ZyBjbGFzcz0iaWNvbiIgdmlld0JveD0iMCAwIDEwMjQgMTAyNCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB3aWR0aD0iMjAiIGhlaWdodD0iMjAiPjxwYXRoIGQ9Ik01NjQgMTgwLjJINDQ4Yy04LjMgMC0xNS02LjctMTUtMTVzNi43LTE1IDE1LTE1aDExNmM4LjIgMCAxNSA2LjcgMTUgMTVzLTYuOCAxNS0xNSAxNXoiIGZpbGw9IiM5ODk4OTgiLz48cGF0aCBkPSJNOTQ1IDk1Mi4ySDgxLjJjLTguMiAwLTE1LTYuNy0xNS0xNVYxNjIuOGMwLTguMyA2LjgtMTUgMTUtMTVIMjk0YzguMiAwIDE1IDYuNyAxNSAxNXMtNi44IDE1LTE1IDE1SDk2LjJ2NzQ0LjRIOTMwVjE3Ny44SDcxMy42Yy04LjMgMC0xNS02LjctMTUtMTVzNi43LTE1IDE1LTE1SDk0NWM4LjIgMCAxNSA2LjcgMTUgMTV2Nzc0LjRjMCA4LjMtNi44IDE1LTE1IDE1eiIgZmlsbD0iIzk4OTg5OCIvPjxwYXRoIGQ9Ik0zMzMuMyA1NTFIMjE2Yy04LjIgMC0xNS02LjgtMTUtMTVzNi44LTE1IDE1LTE1aDExNy4zYzguMyAwIDE1IDYuNiAxNSAxNXMtNi43IDE1LTE1IDE1em0yMzAuMyAwSDQ0Ni4zYy04LjMgMC0xNS02LjgtMTUtMTVzNi43LTE1IDE1LTE1aDExNy4zYzguMiAwIDE1IDYuNiAxNSAxNXMtNi44IDE1LTE1IDE1em0yMzAuMiAwSDY3Ni42Yy04LjMgMC0xNS02LjgtMTUtMTVzNi43LTE1IDE1LTE1aDExNy4yYzguMyAwIDE1IDYuNiAxNSAxNXMtNi43IDE1LTE1IDE1ek0zMzMuMyA3NDBIMjE2Yy04LjIgMC0xNS02LjgtMTUtMTVzNi44LTE1IDE1LTE1aDExNy4zYzguMyAwIDE1IDYuNiAxNSAxNXMtNi43IDE1LTE1IDE1em0yMzAuMyAwSDQ0Ni4zYy04LjMgMC0xNS02LjgtMTUtMTVzNi43LTE1IDE1LTE1aDExNy4zYzguMiAwIDE1IDYuNiAxNSAxNXMtNi44IDE1LTE1IDE1em0yMzAuMiAwSDY3Ni42Yy04LjMgMC0xNS02LjgtMTUtMTVzNi43LTE1IDE1LTE1aDExNy4yYzguMyAwIDE1IDYuNiAxNSAxNXMtNi43IDE1LTE1IDE1ek0zNzAuOCAyNTguNmMtOC4zIDAtMTUtNi43LTE1LTE1Vjg2LjhjMC04LjIgNi43LTE1IDE1LTE1czE1IDYuOCAxNSAxNXYxNTYuOGMwIDguMy02LjcgMTUtMTUgMTV6bTI3MC4yIDBjLTguMyAwLTE1LTYuNy0xNS0xNVY4Ni44YzAtOC4yIDYuNy0xNSAxNS0xNXMxNSA2LjggMTUgMTV2MTU2LjhjMCA4LjMtNi43IDE1LTE1IDE1ek05NDUgMzcyLjJIODEuMmMtOC4yIDAtMTUtNi43LTE1LTE1czYuOC0xNSAxNS0xNUg5NDVjOC4yIDAgMTUgNi43IDE1IDE1cy02LjggMTUtMTUgMTV6IiBmaWxsPSIjOTg5ODk4Ii8+PC9zdmc+') no-repeat 50% 50%;
  }

  .datepicker-wraper {
    display: flex;
    align-items: center;
    margin-top: $sp-margin-small;
    margin-bottom: $sp-margin-small;
  }

  .datepicker-shortcuts {
    >ul{
      list-style: none;
      margin: 0;
      padding: 0;
    }
    >ul li {
      height: $height2;
      padding: $sp-margin-small;
      display: flex;
      align-items: center;
      cursor: pointer;
    }
    >ul li.selected {
      color: white;
      background-color: $primary;
      border-radius: $radius;
    }
  }

  .date-range-picker {
    padding-left: $sp-margin-larger;
    padding-right: $sp-margin-small;
  }

</style>



// WEBPACK FOOTER //
// src/components/explorer/DateRangeSelector.vue